<script setup lang="ts">
import {Contest} from "@/contest/interfaces";
import dateHelper from "@/core/helpers/dateHelper";
import ExpandableTextOverflow from "@/core/components/expandable-text-overflow/ExpandableTextOverflow.vue";
import MarkdownView from "@/core/components/markdown/Markdown.vue";
import ContestTopPerformers from "./ContestTopPerformers.vue";
import {inject, ref, watch} from "vue";
import {contestApi} from "@/container";
import useEnvironment from "@/notification/composables/useEnvironment";
import ContestEdition from "@/contest/components/ContestEdition.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Dialog, DialogContent, DialogTitle} from "@/core/components/ui/dialog";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {AxiosError} from "axios";
import * as Sentry from "@sentry/vue";
import ContestDuplication from "@/contest/components/ContestDuplication.vue";
import ContestActionDropdownMenu from "@/contest/components/ContestActionDropdownMenu.vue";

const props = withDefaults(defineProps<{
    contest: Contest;
    shouldLoad?: boolean;
    hasAction?: boolean;
}>(), {
    shouldLoad: true,
    hasAction: true,
});

const emit = defineEmits(['update:contest']);

const open = ref(false);
const loading = ref(false);
const notSupported = ref(false);
const error = ref(false);
const editionOpen = ref(false);
const duplicationOpen = ref(false);
const {environment} = useEnvironment();
const openConfirmationModal = inject('openConfirmationModal') as (
    title: string,
    message: string,
    success: () => never | Promise<void>
) => Promise<void>;

watch(() => props.shouldLoad, (newValue) => {
    if (!newValue) {
        open.value = !props.hasAction;
    }
});

/**
 * Export contest data.
 */
const exportContest = async () => {
    loading.value = true;
    notSupported.value = false;
    error.value = false;
    try {
        const blob = await contestApi().export(props.contest.id);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `export_${props.contest.name}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        loading.value = false;
    } catch (e) {
        if ((e as AxiosError).response?.status === 403) {
            notSupported.value = true;
        } else {
            error.value = true;
            Sentry.captureException(e);
        }
    }
};

/**
 * Ask a confirmation before deleting of a contest
 */
const confirmDelete = async () => {
    await openConfirmationModal(
        "Supprimer le challenge",
        "Êtes-vous sûr de vouloir supprimer ce challenge ? Cette action est irréversible.",
        () => contestApi().delete(props.contest.id)
            .then(() => emit('update:contest'))
    );
};
</script>

<template>
  <div>
    <Dialog v-model:open="loading">
      <DialogContent>
        <DialogTitle>Export du challenge en cours</DialogTitle>
        <BaseFlexSpinner v-if="!notSupported && !error" />
        <div v-if="notSupported" class="text-center">
          <p>L'export n'est pas supporté pour ce challenge</p>
        </div>
        <div v-if="error" class="text-center">
          <p>Une erreur est survenue lors de l'export</p>
        </div>
      </DialogContent>
    </Dialog>
    <div class="flex gap-4">
      <img
        v-if="props.contest.file?.url"
        :src="props.contest.file.url"
        class="h-32 w-32 object-contain"
        alt="Image contest"
      >
      <div v-else class="h-32 w-32 bg-gray-200 rounded-md shrink-0" />
      <div class="w-full">
        <div class="flex justify-between items-center mb-2 mr-2">
          <h3>{{ props.contest.name }}</h3>
          <ContestActionDropdownMenu
            v-if="environment?.organizationId && props.contest.organization_id === environment.organizationId && hasAction"
            :contest="contest"
            @on-duplication="duplicationOpen = true"
            @on-edition="editionOpen = true"
            @on-export="exportContest"
            @on-delete="confirmDelete"
          >
            <BaseButton density="comfortable">
              Actions
            </BaseButton>
          </ContestActionDropdownMenu>
          <ContestEdition
            v-if="environment?.organizationId && props.contest.organization_id === environment.organizationId"
            v-model:open="editionOpen"
            :contest-id="props.contest.id"
            @on-refresh="emit('update:contest')"
          />
          <ContestDuplication
            v-if="environment?.organizationId && props.contest.organization_id === environment.organizationId"
            v-model:open="duplicationOpen"
            :contest-id="props.contest.id"
            @on-refresh="emit('update:contest')"
          />
        </div>
        <p v-if="props.contest.end_date" class="text-sm text-gray-500">
          Du {{ dateHelper.shortFormat(new Date(props.contest.start_date)) }} au
          {{ dateHelper.shortFormat(new Date(props.contest.end_date)) }}
        </p>
        <p v-else class="text-sm text-gray-500">
          A partir du {{ dateHelper.shortFormat(new Date(props.contest.start_date)) }}
        </p>
        <ExpandableTextOverflow
          v-model:model-value="open" class="w-full" size="single-line"
          :background-color="{ red: 255, green: 255, blue: 255 }"
        >
          <MarkdownView :content="props.contest.description" />
        </ExpandableTextOverflow>
      </div>
    </div>
    <ContestTopPerformers :contest="props.contest" :should-load="props.shouldLoad" />
  </div>
</template>

<style scoped>

</style>
